import {
  OWNER,
  type AreaId,
  type DocumentKind,
  type Voice,
} from '@/james-playground/aa-family-claim/claim/asks';
import {
  propose,
  type Facts,
  type SourceId,
} from '@/james-playground/aa-family-claim/claim/facts';
import {
  NO_ROUNDS,
  applies,
  type Rounds,
} from '@/james-playground/aa-family-claim/claim/engine';
import { ENTRY, entry } from '@/james-playground/aa-family-claim/claim/registry';
import {
  SCENARIO,
  proposalsOf,
  type Proposal,
  type ScenarioId,
} from '@/james-playground/aa-family-claim/claim/scenarios';

/**
 * One claim in progress: what it knows, which rounds have run, and what has
 * arrived since the family last looked. Pure functions only; the screens hold
 * the state.
 */
export type ClaimState = {
  scenario: ScenarioId;
  facts: Facts;
  rounds: Rounds;
  /** Their say-so for AI reading what they say, type or photograph. */
  aiConsent?: 'yes' | 'no';
  /** Entries that arrived from a Care Adviser call, not yet seen on the hub. */
  arrivals: string[];
  /** What each finished round added, newest last, for the hub's confirmation. */
  log: { area?: AreaId; added: string[] }[];
};

export const voiceOf = (state: Pick<ClaimState, 'scenario'>): Voice => {
  const s = SCENARIO[state.scenario];
  return { name: s.claimant.first, selfClaim: s.selfClaim };
};

export const apply = (facts: Facts, proposals: Proposal[], source: SourceId): Facts =>
  proposals.reduce((next, p) => propose(next, p.id, p.value, source, p.quote), facts);

/** A new claim for a scenario: profile and account facts as proposals to confirm. */
export const startClaim = (scenario: ScenarioId): ClaimState => {
  const s = SCENARIO[scenario];
  let facts = apply({}, s.profile, 'profile');
  facts = apply(facts, s.account, 'account');
  const notes = s.callNotes?.proposals ?? [];
  facts = apply(facts, notes, 'call_notes');
  return {
    scenario,
    facts,
    rounds: NO_ROUNDS,
    arrivals: notes.map((p) => p.id),
    log: [],
  };
};

/** The entries a set of proposals actually landed on, for the found card. */
export const landed = (before: Facts, after: Facts, proposals: Proposal[]): string[] =>
  [...new Set(proposals.map((p) => p.id))].filter(
    (id) =>
      ENTRY[id] &&
      after[id] !== before[id] &&
      applies(entry(id), after) !== false,
  );

/** Run a scripted voice note. Proposals land as "proposed", with their quotes. */
export const hearVoice = (state: ClaimState, promptId: string): { state: ClaimState; added: string[] } => {
  const recording = SCENARIO[state.scenario].voice[promptId];
  const proposals = recording ? proposalsOf(recording) : [];
  const facts = apply(state.facts, proposals, 'voice');
  const added = landed(state.facts, facts, proposals);
  const rounds: Rounds =
    promptId === 'voice-opening'
      ? { ...state.rounds, openingDone: true }
      : { ...state.rounds, voiceDone: [...state.rounds.voiceDone, promptId] };
  return { state: { ...state, facts, rounds }, added };
};

/** Read a document photo. Each kind proposes only what it can say. */
export const readDocument = (state: ClaimState, kind: DocumentKind): { state: ClaimState; added: string[] } => {
  const doc = SCENARIO[state.scenario].documents[kind];
  const proposals = doc?.proposals ?? [];
  const facts = apply(state.facts, proposals, kind);
  return {
    state: {
      ...state,
      facts,
      rounds: { ...state.rounds, documentsDone: [...state.rounds.documentsDone, kind] },
    },
    added: landed(state.facts, facts, proposals),
  };
};

/**
 * A typed account, read sentence by sentence. Each sentence that mentions an
 * activity proposes that activity's Yes/No gate, quoting the sentence. Night
 * sentences only ever answer night questions, so "she needs the toilet at
 * night" is not read as help with the toilet in the day.
 */
const TYPED_TOPICS: { id: string; words: RegExp; night?: boolean }[] = [
  { id: 'nightNeedsHelp', words: /\b(night|nights|overnight|wakes?|bedtime)\b/i, night: true },
  { id: 'nightSupervisionNeeded', words: /\b(sleep over|stay(s)? awake|watch over)\b/i, night: true },
  { id: 'bedNeedsHelp', words: /\b(out of bed|into bed|get(ting)? up|gets up)\b/i },
  { id: 'toiletNeedsHelp', words: /\b(toilet|commode|continence|incontinen\w*|pads?)\b/i },
  { id: 'washingNeedsHelp', words: /\b(wash\w*|bath\w*|shower\w*)\b/i },
  { id: 'dressingNeedsHelp', words: /\b(dress\w*|clothes|socks|shoes|buttons?)\b/i },
  { id: 'movingIndoorsNeedsHelp', words: /\b(stairs|walk\w*|frame|stick|chair)\b/i },
  { id: 'eatingNeedsHelp', words: /\b(eat\w*|meals?|food|drink\w*)\b/i },
  { id: 'medicationNeedsHelp', words: /\b(tablets?|medicines?|medication|pills?)\b/i },
  { id: 'communicationNeedsHelp', words: /\b(post|letters|phone|forms|remember\w*)\b/i },
  { id: 'daySupervisionNeeded', words: /\b(left alone|on (her|my) own|wander\w*|confus\w*)\b/i },
];

export const readTyped = (
  state: ClaimState,
  typed: string,
  promptId = 'voice-opening',
): { state: ClaimState; added: string[] } => {
  const sentences = typed.split(/(?<=[.!?])\s+|\n+/).map((s) => s.trim()).filter(Boolean);
  const proposals: Proposal[] = [];
  for (const sentence of sentences) {
    const isNight = TYPED_TOPICS[0].words.test(sentence);
    for (const topic of TYPED_TOPICS) {
      if (isNight && !topic.night) continue;
      if (topic.words.test(sentence) && !proposals.some((p) => p.id === topic.id))
        proposals.push({ id: topic.id, value: 'Yes', quote: sentence });
    }
  }
  const facts = apply(state.facts, proposals, 'typed');
  // Typing answers a voice prompt as fully as talking does.
  const rounds: Rounds =
    promptId === 'voice-opening'
      ? { ...state.rounds, openingDone: true }
      : { ...state.rounds, voiceDone: [...state.rounds.voiceDone, promptId] };
  return { state: { ...state, facts, rounds }, added: landed(state.facts, facts, proposals) };
};

/** Close a round: the log entry drives "Added 6 answers to Help in the day". */
export const finishRound = (state: ClaimState, added: string[], area?: AreaId): ClaimState => ({
  ...state,
  log: [...state.log, { area: area ?? (added[0] ? OWNER[added[0]]?.area : undefined), added }],
});
